// game.js — level system + sprites + keyboard + mobile arrows
// FIXED: HUD overlays the maze (no canvas shift), aligned in one row: Level + hearts + Luxe level progress
// FIXED: left-side sprite showcase sits OUTSIDE maze (left) and larger than in-game sprites
// FIXED: arrows stacked vertically to the RIGHT of the maze (no overlap)
// FIXED: Level Complete panel is a clickable overlay centered over the maze
// CRITICAL: everything stays inside the existing IIFE scope (no broken start button / frozen vars)
//
// ✅ MOBILE FIXES ADDED:
// - On mobile (<= 820px): do NOT absolute-position D-pad; let CSS grid place it under the canvas (compass layout)
// - On mobile (<= 820px): move HUD ABOVE canvas, in normal flow, and make it BLACK (text only now)
// - ✅ NEW: On mobile, stack HUD into 2 lines:
//   Line 1: Level + hearts
//   Line 2: Luxe Level (percentage only)
// - ✅ UPDATED: Hard-block press-and-hold highlight/callout + double-tap behaviors on controls
// - ✅ UPDATED: Fix “hold randomly releases” by NOT using pointerleave to release on touch
// - ✅ NEW: Mobile controls only move when the ARROW IMAGE itself is touched (not the empty hitbox around it)
// - Resize/orientation updates call the same positioning functions safely

(() => {
  const startBtn = document.getElementById("startGame");
  const stage = document.getElementById("playStage");
  const canvas = document.getElementById("playCanvas");
  const ctx = canvas?.getContext("2d");

  const levelLabel = document.getElementById("levelLabel");
  // ✅ BAR REMOVED: keep text-only Luxe Level
  const luxuryFill = null;
  const heartsEl = document.getElementById("hearts");

  const levelCompleteEl = document.getElementById("levelComplete");
  const completeBagImg = document.getElementById("completeBagImg");
  const nextLevelBtn = document.getElementById("nextLevel");
  const replayBtn = document.getElementById("replayLevel");
  const viewBagLink = document.getElementById("viewBagLink");

  // ✅ NEW: end-of-game subscribe bits (from Liquid)
  const subscribeWrap = document.getElementById("subscribeWrap");
  const completeTitleEl = document.getElementById("completeTitle");

  const btnUp = document.getElementById("btnUp");
  const btnDown = document.getElementById("btnDown");
  const btnLeft = document.getElementById("btnLeft");
  const btnRight = document.getElementById("btnRight");

  const arrowUpImg = document.getElementById("arrowUp");
  const arrowDownImg = document.getElementById("arrowDown");
  const arrowLeftImg = document.getElementById("arrowLeft");
  const arrowRightImg = document.getElementById("arrowRight");

  if (!startBtn || !stage || !canvas || !ctx) return;

  // Optional but helpful: reduce scroll/bounce weirdness near controls
  stage.style.overscrollBehavior = "none";

  // --- Mobile layout detection (matches your CSS breakpoint)
  const mqMobile = window.matchMedia("(max-width: 820px)");
  const isMobileLayout = () => mqMobile.matches;

  // ---- Constants
  const TILE = 34;
  const BOARD = TILE * 21;

  // Keep buffer fixed (no resize during gameplay). CSS scaling only.
  canvas.width = BOARD;
  canvas.height = BOARD;
  canvas.style.width = "100%";
  canvas.style.height = "auto";
  canvas.style.display = "block";
  canvas.style.position = "relative";
  canvas.style.zIndex = "1";

  // ---- Sprite sizing
  const SPRITE_OVERHANG = 28;
  const SPRITE_DRAW_SIZE = TILE + SPRITE_OVERHANG * 2;
  const SPRITE_XY_OFFSET = -SPRITE_OVERHANG;

  // ---- Vault bag sizing (bigger)
  const VAULT_BAG_OVER = 18;

  // ---- Showcase sizing (bigger than game sprites)
  const SHOWCASE_SIZE = 92;
  const SHOWCASE_GUTTER = 18;

  // ---- Dpad layout (stacked vertical to the right of the maze - desktop)
  const DPAD_GUTTER = 22;
  const DPAD_GAP = 14;

  // ---- Sprite URLs (exact)
  const URLS = {
    player:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/Untitled_design_4.png?v=1771042966",
    toddlerAwake:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/7.png?v=1771016014",
    toddlerAsleep:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/6.png?v=1771016014",
    pacifier:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/1_aa6167f4-5cb2-41ab-bfa8-2bc04b3b57f1.png?v=1771016013",
    lipstick:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/5_09e7ff84-a6ed-482e-9ff5-98c9c9b199f0.png?v=1771016014",
    cash:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/4_a0b84b54-ad78-4b9e-b718-67ea97bd3333.png?v=1771016014",
    door:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/Untitled_design_2_db9d4172-103d-4d68-836a-406fab0f5df9.png?v=1771040583",

    // arrows idle
    arrowUp:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/1_12bfce62-62c3-45ee-adfa-11ceab8cdd27.png?v=1771018168",
    arrowDown:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/3_ae5b0b27-fc02-4ce3-b354-2ac625fe4251.png?v=1771018168",
    arrowRight:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/2_ee6e1af0-a5c9-4de4-a21f-4d6da9faf699.png?v=1771018168",
    arrowLeft:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/4_da0dfd10-454e-4234-87b3-bb4a78352caa.png?v=1771018168",

    // arrows pressed
    arrowUpOn:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/5_e6a5177d-316e-4319-a259-65c1c0780791.png?v=1771018168",
    arrowDownOn:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/7_79f547b1-46bb-416f-88d3-4a99caa017cf.png?v=1771018168",
    arrowRightOn:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/6_c9253d28-e0e4-4f2e-a023-5e70dd006e90.png?v=1771018168",
    arrowLeftOn:
      "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/8.png?v=1771018168",
  };

  const LEVEL_BAG_IMAGES = [
    "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/6_53cffac0-ea84-4499-83b3-9a8585d7a1f0.png?v=1771016446",
    "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/3_d994e880-dfc6-4dd4-a047-fbd19aef239b.png?v=1771016446",
    "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/5_4a08c387-dace-42a7-b5bd-9062bc298bbb.png?v=1771016446",
    "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/4_a091b31d-6fb0-406b-ae8a-da3300875550.png?v=1771016446",
    "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/2_5df52d23-8093-4926-bbc5-eed18ca080b6.png?v=1771016446",
    "https://cdn.shopify.com/s/files/1/0933/8748/9601/files/1_f8506810-fe3c-4612-969f-1fca6318fc7c.png?v=1771016446",
  ];

  // ✅ Product page links per level (Level 1 -> Level 6)
  const LEVEL_BAG_LINKS = [
    "https://jtvsky.com/products/gucci-1955-horsebit-mini-top-handle-bag-italian-secondhand?variant=51393508606273",
    "https://jtvsky.com/products/fendi-black-zucca-monogram-baguette-shoulder-bag-italian-designer-vintage?variant=51337897476417",
    "https://jtvsky.com/products/ralph-lauren-ricky-top-handle-bag-italy-made-japan-exclusive?variant=51373255000385",
    "https://jtvsky.com/products/chanel-chocolate-bar-black-patent-leather-tote-french-designer-vintage?variant=51337911599425",
    "https://jtvsky.com/products/dolce-gabbana-byzantine-mosaic-print-bag-italian-designer-archive?variant=51288376738113",
    "https://jtvsky.com/products/chanel-small-red-classic-single-flap-bag-french-designer-vintage?variant=51390429823297",
  ];

  function getLevelBagLink(lvl) {
    return LEVEL_BAG_LINKS[lvl - 1] || "#";
  }

  function wireWinModalLinkForLevel(lvl) {
    const href = getLevelBagLink(lvl);

    if (viewBagLink) {
      viewBagLink.href = href;
      viewBagLink.target = "_self";
      viewBagLink.rel = "noopener";
      viewBagLink.style.cursor = href === "#" ? "default" : "pointer";
      viewBagLink.setAttribute("aria-disabled", href === "#" ? "true" : "false");
    }

    if (completeBagImg) {
      completeBagImg.style.cursor = href === "#" ? "default" : "pointer";
      completeBagImg.onclick = null;
      if (href !== "#") {
        completeBagImg.onclick = () => {
          window.location.href = href;
        };
      }
    }
  }

  function applyEndOfGameUI() {
    const isFinal = level === 6;
    if (completeTitleEl) {
      completeTitleEl.textContent = isFinal ? "Game Complete" : "Level Complete";
    }
    if (subscribeWrap) subscribeWrap.hidden = !isFinal;
    if (nextLevelBtn) nextLevelBtn.hidden = isFinal;
  }

  // ---- Load images
  const img = {};
  const loadImage = (key, src) =>
    new Promise((resolve) => {
      const i = new Image();
      i.decoding = "async";
      i.loading = "eager";
      i.onload = () => resolve(i);
      i.onerror = () => resolve(i);
      i.src = src;
      img[key] = i;
    });

  let assetsLoaded = false;

  async function loadAll() {
    if (assetsLoaded) return;

    const entries = Object.entries(URLS);
    for (const [k, v] of entries) await loadImage(k, v);

    for (let i = 0; i < LEVEL_BAG_IMAGES.length; i++) {
      await loadImage(`levelBag${i + 1}`, LEVEL_BAG_IMAGES[i]);
    }

    if (arrowUpImg) arrowUpImg.src = URLS.arrowUp;
    if (arrowDownImg) arrowDownImg.src = URLS.arrowDown;
    if (arrowLeftImg) arrowLeftImg.src = URLS.arrowLeft;
    if (arrowRightImg) arrowRightImg.src = URLS.arrowRight;

    assetsLoaded = true;
  }

  // ---------------------------------------------------------------------------
  // HUD: desktop overlays maze; mobile sits ABOVE maze and stacks into 2 lines
  // ---------------------------------------------------------------------------
  let hudRowEl = null;
  let hudTopEl = null;
  let luxeWrapEl = null;
  let luxeTextEl = null;

  function ensureHudRow() {
    if (hudRowEl) return;

    hudRowEl = document.createElement("div");
    hudRowEl.setAttribute("aria-hidden", "true");
    hudRowEl.className = "game-hudRow";

    Object.assign(hudRowEl.style, {
      position: "absolute",
      display: "flex",
      alignItems: "center",
      gap: "12px",
      zIndex: "20",
      pointerEvents: "none",
      whiteSpace: "nowrap",
    });

    // top line wrapper (Level + hearts)
    hudTopEl = document.createElement("div");
    hudTopEl.className = "game-hudTop";
    Object.assign(hudTopEl.style, {
      display: "flex",
      alignItems: "center",
      gap: "12px",
      pointerEvents: "none",
      whiteSpace: "nowrap",
    });

    if (levelLabel) hudTopEl.appendChild(levelLabel);
    if (heartsEl) hudTopEl.appendChild(heartsEl);

    // Luxe line wrapper (TEXT ONLY)
    luxeWrapEl = document.createElement("div");
    luxeWrapEl.className = "game-luxeWrap";
    Object.assign(luxeWrapEl.style, {
      display: "flex",
      alignItems: "center",
      gap: "10px",
      pointerEvents: "none",
      whiteSpace: "nowrap",
    });

    luxeTextEl = document.createElement("span");
    luxeTextEl.className = "game-luxeText";
    luxeTextEl.textContent = "LUXE LEVEL 0%";
    Object.assign(luxeTextEl.style, {
      fontSize: "14px",
      lineHeight: "1",
      opacity: "1",
      color: "#ffffff",
      fontWeight: "700",
    });

    luxeWrapEl.appendChild(luxeTextEl);

    // Build row: [top line] + [luxe line]
    hudRowEl.appendChild(hudTopEl);
    hudRowEl.appendChild(luxeWrapEl);

    stage.appendChild(hudRowEl);

    if (levelLabel) {
      levelLabel.style.margin = "0";
      levelLabel.style.padding = "0";
      levelLabel.style.lineHeight = "1";
      levelLabel.style.color = "#ffffff";
      levelLabel.style.textTransform = "uppercase";
    }

    if (heartsEl) {
      heartsEl.style.margin = "0";
      heartsEl.style.padding = "0";
      heartsEl.style.lineHeight = "1";
      heartsEl.style.display = "flex";
      heartsEl.style.gap = "6px";
      heartsEl.style.alignItems = "center";
      heartsEl.style.height = "28px";
      heartsEl.style.minWidth = "96px";
    }
  }

  function applyHudColorsForLayout() {
    if (isMobileLayout()) {
      if (levelLabel) levelLabel.style.color = "#000000";
      if (heartsEl) heartsEl.style.color = "#000000";
      if (luxeTextEl) luxeTextEl.style.color = "#000000";
    } else {
      if (levelLabel) levelLabel.style.color = "#ffffff";
      if (heartsEl) heartsEl.style.color = "#ffffff";
      if (luxeTextEl) luxeTextEl.style.color = "#ffffff";
    }
  }

  function positionHud() {
    if (!hudRowEl) return;

    if (isMobileLayout()) {
      hudRowEl.style.position = "static";
      hudRowEl.style.left = "";
      hudRowEl.style.top = "";
      hudRowEl.style.marginBottom = "8px";
      hudRowEl.style.whiteSpace = "normal";

      if (stage.firstChild !== hudRowEl) stage.insertBefore(hudRowEl, canvas);

      applyHudColorsForLayout();
      return;
    }

    hudRowEl.style.position = "absolute";
    hudRowEl.style.marginBottom = "0";
    hudRowEl.style.whiteSpace = "nowrap";

    requestAnimationFrame(() => {
      const stageRect = stage.getBoundingClientRect();
      const canvasRect = canvas.getBoundingClientRect();
      const left = canvasRect.left - stageRect.left + 12;
      const top = canvasRect.top - stageRect.top + 10;
      hudRowEl.style.left = `${left}px`;
      hudRowEl.style.top = `${top}px`;
      applyHudColorsForLayout();
    });
  }

  function applyHudLayout() {
    stage.style.position = stage.style.position || "relative";
    stage.style.overflow = "visible";
    stage.style.paddingTop = "0";
    ensureHudRow();
    positionHud();
    canvas.style.display = "block";
  }

  // ---------------------------------------------------------------------------
  // WIN MODAL: centered on screen (viewport), clickable overlay
  // ---------------------------------------------------------------------------
  let winCloseBtn = null;

  function clearPressedInput() {
    pressed.up = pressed.down = pressed.left = pressed.right = false;
    setArrowVisual("up", false);
    setArrowVisual("down", false);
    setArrowVisual("left", false);
    setArrowVisual("right", false);
  }

  function closeWinModalToHome() {
    stopLoop();
    clearPressedInput();

    level = 1;
    applyLevelDifficulty();
    resetLevel();

    if (levelCompleteEl) levelCompleteEl.hidden = true;
    stage.hidden = true;
    startBtn.disabled = false;
  }

  function ensureWinCloseButton() {
    if (!levelCompleteEl) return;
    if (winCloseBtn && levelCompleteEl.contains(winCloseBtn)) return;

    winCloseBtn = document.createElement("button");
    winCloseBtn.type = "button";
    winCloseBtn.setAttribute("aria-label", "Close");
    winCloseBtn.textContent = "×";

    Object.assign(winCloseBtn.style, {
      position: "absolute",
      top: "10px",
      right: "10px",
      width: "36px",
      height: "36px",
      borderRadius: "10px",
      border: "1px solid rgba(0,0,0,0.15)",
      background: "rgba(255,255,255,0.95)",
      color: "#000",
      fontSize: "26px",
      lineHeight: "1",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      zIndex: "10001",
      padding: "0",
      pointerEvents: "auto",
      WebkitTapHighlightColor: "rgba(0,0,0,0)",
      touchAction: "manipulation",
    });

    winCloseBtn.addEventListener(
      "click",
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeWinModalToHome();
      },
      { passive: false }
    );

    levelCompleteEl.appendChild(winCloseBtn);
  }

  function applyWinModalLayout() {
    if (!levelCompleteEl) return;

    Object.assign(levelCompleteEl.style, {
      position: "fixed",
      left: "50%",
      top: "50%",
      transform: "translate(-50%, -50%)",
      zIndex: "9999",
      width: "min(92vw, 350px)",
      maxHeight: "92vh",
      overflow: "auto",
      boxShadow: "0 18px 60px rgba(0,0,0,0.22)",
      borderRadius: "16px",
      background: "white",
      pointerEvents: "auto",
    });

    // Ensure modal can anchor the X button
    levelCompleteEl.style.position = "fixed";
    ensureWinCloseButton();
  }

  // ---- Dpad positioning
  function positionDpad() {
    const buttons = [btnUp, btnLeft, btnRight, btnDown].filter(Boolean);
    if (!buttons.length) return;

    if (isMobileLayout()) {
      buttons.forEach((btn) => {
        btn.style.position = "";
        btn.style.left = "";
        btn.style.top = "";
        btn.style.right = "";
        btn.style.zIndex = "";
        btn.style.touchAction = "none";
      });
      return;
    }

    requestAnimationFrame(() => {
      const stageRect = stage.getBoundingClientRect();
      const canvasRect = canvas.getBoundingClientRect();

      const x = canvasRect.right - stageRect.left + DPAD_GUTTER;
      const topStart = canvasRect.top - stageRect.top + 32;

      const applyBtn = (btn, top) => {
        btn.style.position = "absolute";
        btn.style.left = `${x}px`;
        btn.style.top = `${top}px`;
        btn.style.right = "auto";
        btn.style.zIndex = "8";
        btn.style.touchAction = "none";
      };

      let t = topStart;
      if (btnUp) {
        applyBtn(btnUp, t);
        t += (btnUp.offsetHeight || 52) + DPAD_GAP;
      }
      if (btnLeft) {
        applyBtn(btnLeft, t);
        t += (btnLeft.offsetHeight || 52) + DPAD_GAP;
      }
      if (btnRight) {
        applyBtn(btnRight, t);
        t += (btnRight.offsetHeight || 52) + DPAD_GAP;
      }
      if (btnDown) {
        applyBtn(btnDown, t);
      }
    });
  }

  // ---- Left-side sprite showcase
  let spriteShowcaseEl = null;

  function ensureSpriteShowcase() {
    if (spriteShowcaseEl) return;

    spriteShowcaseEl = document.createElement("div");
    spriteShowcaseEl.setAttribute("aria-hidden", "true");

    Object.assign(spriteShowcaseEl.style, {
      position: "absolute",
      display: "flex",
      flexDirection: "column",
      gap: "14px",
      alignItems: "center",
      pointerEvents: "none",
      zIndex: "9",
    });

    const makeImg = (src) => {
      const el = document.createElement("img");
      el.src = src;
      el.alt = "";
      Object.assign(el.style, {
        width: `${SHOWCASE_SIZE}px`,
        height: `${SHOWCASE_SIZE}px`,
        display: "block",
        imageRendering: "pixelated",
      });
      return el;
    };

    spriteShowcaseEl.appendChild(makeImg(URLS.player));
    spriteShowcaseEl.appendChild(makeImg(URLS.toddlerAwake));
    spriteShowcaseEl.appendChild(makeImg(URLS.pacifier));
    spriteShowcaseEl.appendChild(makeImg(URLS.lipstick));
    spriteShowcaseEl.appendChild(makeImg(URLS.cash));

    stage.appendChild(spriteShowcaseEl);
    positionShowcase();

    window.addEventListener("resize", positionShowcase, { passive: true });
  }

  function positionShowcase() {
    if (!spriteShowcaseEl) return;

    if (isMobileLayout()) {
      spriteShowcaseEl.style.display = "none";
      return;
    }

    spriteShowcaseEl.style.display = "flex";

    requestAnimationFrame(() => {
      const stageRect = stage.getBoundingClientRect();
      const canvasRect = canvas.getBoundingClientRect();
      const showcaseWidth = spriteShowcaseEl.getBoundingClientRect().width || SHOWCASE_SIZE;

      const left = canvasRect.left - stageRect.left - showcaseWidth - SHOWCASE_GUTTER;
      const top = canvasRect.top - stageRect.top + 10;

      spriteShowcaseEl.style.left = `${left}px`;
      spriteShowcaseEl.style.top = `${top}px`;
    });
  }

  // ---- Maze (swap per level later)
  const levelGrid = [
    [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
    [0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,0],
    [0,1,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0,1,0,1,0],
    [0,1,0,1,0,1,0,1,1,1,1,1,0,1,0,1,0,1,0,1,0],
    [0,1,0,1,0,1,0,1,0,0,0,1,0,1,0,1,0,1,0,1,0],
    [0,1,1,1,1,1,1,1,0,1,1,1,1,1,1,1,1,1,1,1,0],
    [0,1,0,0,0,1,0,1,0,1,0,0,0,1,0,1,0,0,0,1,0],
    [0,1,0,1,1,1,0,1,1,1,1,1,0,1,1,1,0,1,1,1,0],
    [0,1,0,1,0,0,0,0,0,1,0,0,0,0,0,1,0,1,0,0,0],
    [0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,0],
    [0,1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,1,0],
    [0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,1,0],
    [0,1,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0,1,0,1,0],
    [0,1,0,1,0,1,0,1,1,1,1,1,0,1,0,1,0,1,0,1,0],
    [0,1,0,1,0,1,0,1,0,0,0,1,0,1,0,1,0,1,0,1,0],
    [0,1,1,1,1,1,1,1,0,1,1,1,1,1,1,1,1,1,1,1,0],
    [0,2,0,0,0,1,0,1,0,1,0,0,0,1,0,1,0,0,0,1,0],
    [0,1,0,1,1,1,0,1,1,1,1,1,0,1,1,1,0,1,1,1,0],
    [0,1,0,1,0,0,0,0,0,1,0,0,0,0,0,1,0,1,0,0,0],
    [0,1,1,1,1,1,1,1,1,1,1,1,1,1,1,3,4,1,1,1,0],
    [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
  ];

  // Colors
  const PATH_BG = "#efefef";
  const DOOR_GOLD = "#c8a23a";
  const VAULT_FLOOR = "#dcdcdc";

  // ---- Game state
  let running = false;
  let rafId = 0;

  let level = 1;
  let hearts = 3;
  let luxury = 0; // internal (still +10 per pickup)
  let doorOpen = false;

  let player = { r: 16, c: 1 };
  let toddler = { r: 1, c: 19 };
  let toddlerSleepUntil = 0;

  let pacifierPos = null; // {r,c}
  let bonusPos = null; // {r,c,type}

  const pressed = { up: false, down: false, left: false, right: false };

  const THRESHOLDS = [35, 45, 55, 65, 75, 85];

  function setHeartsUI() {
    if (!heartsEl) return;
    heartsEl.innerHTML = "";
    for (let i = 0; i < 3; i++) {
      const s = document.createElement("span");
      s.setAttribute("aria-hidden", "true");
      s.textContent = "🩷";
      if (i >= hearts) s.style.opacity = "0";
      heartsEl.appendChild(s);
    }
  }

  // ✅ TEXT ONLY: show 0..100% progress toward unlock threshold for this level
  function setLuxuryUI() {
    const threshold = THRESHOLDS[level - 1] ?? 70;
    const pct =
      threshold > 0
        ? Math.max(0, Math.min(100, Math.round((luxury / threshold) * 100)))
        : 0;
    if (luxeTextEl) luxeTextEl.textContent = `LUXE LEVEL ${pct}%`;
  }

  function findSpawn() {
    for (let r = 0; r < 21; r++) {
      for (let c = 0; c < 21; c++) {
        if (levelGrid[r][c] === 2) return { r, c };
      }
    }
    return { r: 1, c: 1 };
  }

  function isWalkable(r, c) {
    const v = levelGrid[r]?.[c];
    if (v == null) return false;
    if (v === 0) return false;
    if (v === 3 && !doorOpen) return false;
    return true;
  }

  function randomPathTile(exclude = []) {
    const tries = 500;
    for (let t = 0; t < tries; t++) {
      const r = 1 + Math.floor(Math.random() * 19);
      const c = 1 + Math.floor(Math.random() * 19);
      if (!isWalkable(r, c)) continue;
      if (levelGrid[r][c] === 4) continue;
      const hit = exclude.some((p) => p.r === r && p.c === c);
      if (hit) continue;
      return { r, c };
    }
    return null;
  }

  function spawnItems() {
    if (!pacifierPos) pacifierPos = randomPathTile([player, toddler]);

    if (!bonusPos) {
      const type = Math.random() < 0.5 ? "lipstick" : "cash";
      const pos = randomPathTile([player, toddler, ...(pacifierPos ? [pacifierPos] : [])]);
      if (pos) bonusPos = { ...pos, type };
    }
  }

  function moveEntity(entity, dir) {
    const drdc = { up: [-1, 0], down: [1, 0], left: [0, -1], right: [0, 1] }[dir];
    if (!drdc) return;

    const nr = entity.r + drdc[0];
    const nc = entity.c + drdc[1];

    if (isWalkable(nr, nc)) {
      entity.r = nr;
      entity.c = nc;
    }
  }

  function toddlerStep(now) {
    if (now < toddlerSleepUntil) return;

    const options = ["up", "down", "left", "right"];
    let best = null;
    let bestDist = Infinity;

    for (const d of options) {
      const drdc = { up: [-1, 0], down: [1, 0], left: [0, -1], right: [0, 1] }[d];
      const nr = toddler.r + drdc[0];
      const nc = toddler.c + drdc[1];
      if (!isWalkable(nr, nc)) continue;

      const dist = Math.abs(player.r - nr) + Math.abs(player.c - nc);
      if (dist < bestDist) {
        bestDist = dist;
        best = d;
      }
    }

    if (best) moveEntity(toddler, best);
  }

  function checkPickups(now) {
    if (pacifierPos && player.r === pacifierPos.r && player.c === pacifierPos.c) {
      pacifierPos = null;
      toddlerSleepUntil = now + 1000;
    }

    if (bonusPos && player.r === bonusPos.r && player.c === bonusPos.c) {
      bonusPos = null;
      luxury = Math.min(100, luxury + 10);
      setLuxuryUI();
    }

    const threshold = THRESHOLDS[level - 1] ?? 70;
    if (!doorOpen && luxury >= threshold) doorOpen = true;
  }

  function checkHit() {
    if (player.r === toddler.r && player.c === toddler.c) {
      hearts = Math.max(0, hearts - 1);
      setHeartsUI();

      player = { ...findSpawn() };
      toddler = { r: 1, c: 19 };

      if (hearts <= 0) resetLevel();
    }
  }

  function checkWin() {
    if (!doorOpen) return false;
    for (let r = 0; r < 21; r++) {
      for (let c = 0; c < 21; c++) {
        if (levelGrid[r][c] === 4 && player.r === r && player.c === c) return true;
      }
    }
    return false;
  }

  function resetLevel() {
    hearts = 3;
    luxury = 0;
    doorOpen = false;

    player = { ...findSpawn() };
    toddler = { r: 1, c: 19 };

    pacifierPos = null;
    bonusPos = null;
    toddlerSleepUntil = 0;

    setHeartsUI();
    setLuxuryUI();

    if (levelLabel) levelLabel.textContent = `Level ${level}`;
    if (levelCompleteEl) levelCompleteEl.hidden = true;

    if (completeTitleEl) completeTitleEl.textContent = "Level Complete";
    if (subscribeWrap) subscribeWrap.hidden = true;
    if (nextLevelBtn) nextLevelBtn.hidden = false;

    rebuildStaticCache();
    positionShowcase();
    positionDpad();
    positionHud();
    applyWinModalLayout();
  }

  // ---- Offscreen cache for static background
  const bgCanvas = document.createElement("canvas");
  bgCanvas.width = BOARD;
  bgCanvas.height = BOARD;
  const bgCtx = bgCanvas.getContext("2d");

  function drawBagAtTileTo(targetCtx, image, r, c) {
    if (!image) return;
    const over = VAULT_BAG_OVER;
    const x = c * TILE - over;
    const y = r * TILE - over;
    const size = TILE + over * 2;
    targetCtx.drawImage(image, x, y, size, size);
  }

  function rebuildStaticCache() {
    if (!bgCtx) return;

    bgCtx.imageSmoothingEnabled = false;
    bgCtx.clearRect(0, 0, bgCanvas.width, bgCanvas.height);

    bgCtx.fillStyle = PATH_BG;
    bgCtx.fillRect(0, 0, bgCanvas.width, bgCanvas.height);

    for (let r = 0; r < 21; r++) {
      for (let c = 0; c < 21; c++) {
        if (levelGrid[r][c] === 4) {
          bgCtx.fillStyle = VAULT_FLOOR;
          bgCtx.fillRect(c * TILE, r * TILE, TILE, TILE);
        }
      }
    }

    bgCtx.fillStyle = "#222";
    for (let r = 0; r < 21; r++) {
      for (let c = 0; c < 21; c++) {
        if (levelGrid[r][c] === 0) {
          bgCtx.fillRect(c * TILE, r * TILE, TILE, TILE);
        }
      }
    }

    const bagImg = img[`levelBag${level}`];
    if (bagImg) {
      for (let r = 0; r < 21; r++) {
        for (let c = 0; c < 21; c++) {
          if (levelGrid[r][c] === 4) drawBagAtTileTo(bgCtx, bagImg, r, c);
        }
      }
    }
  }

  function drawSpriteAtTile(image, r, c) {
    if (!image) return;
    const x = c * TILE + SPRITE_XY_OFFSET;
    const y = r * TILE + SPRITE_XY_OFFSET;
    ctx.drawImage(image, x, y, SPRITE_DRAW_SIZE, SPRITE_DRAW_SIZE);
  }

  function drawDoor(now) {
    if (doorOpen) return;

    for (let r = 0; r < 21; r++) {
      for (let c = 0; c < 21; c++) {
        if (levelGrid[r][c] === 3) {
          const x = c * TILE;
          const y = r * TILE;

          const threshold = THRESHOLDS[level - 1] ?? 70;
          const near = luxury >= threshold - 10;
          const flicker = near && Math.floor(now / 120) % 2 === 0;

          const doorImg = img.door;
          if (doorImg && doorImg.complete && doorImg.naturalWidth > 0) {
            ctx.save();
            ctx.globalAlpha = flicker ? 0.55 : 1;
            const inset = 2;
            ctx.drawImage(doorImg, x + inset, y + inset, TILE - inset * 2, TILE - inset * 2);
            ctx.restore();
          } else {
            ctx.fillStyle = flicker ? "#f1d37a" : DOOR_GOLD;
            ctx.fillRect(x + 6, y + 6, TILE - 12, TILE - 12);
          }
        }
      }
    }
  }

  function drawFrame(now) {
    ctx.imageSmoothingEnabled = false;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    ctx.drawImage(bgCanvas, 0, 0);
    drawDoor(now);

    if (pacifierPos) drawSpriteAtTile(img.pacifier, pacifierPos.r, pacifierPos.c);

    if (bonusPos) {
      const sprite = bonusPos.type === "cash" ? img.cash : img.lipstick;
      drawSpriteAtTile(sprite, bonusPos.r, bonusPos.c);
    }

    const toddlerSprite = now < toddlerSleepUntil ? img.toddlerAsleep : img.toddlerAwake;
    drawSpriteAtTile(toddlerSprite, toddler.r, toddler.c);
    drawSpriteAtTile(img.player, player.r, player.c);
  }

  // ---- Input (keyboard)
  function setArrowVisual(dir, on) {
    if (!arrowUpImg || !arrowDownImg || !arrowLeftImg || !arrowRightImg) return;
    if (dir === "up") arrowUpImg.src = on ? URLS.arrowUpOn : URLS.arrowUp;
    if (dir === "down") arrowDownImg.src = on ? URLS.arrowDownOn : URLS.arrowDown;
    if (dir === "left") arrowLeftImg.src = on ? URLS.arrowLeftOn : URLS.arrowLeft;
    if (dir === "right") arrowRightImg.src = on ? URLS.arrowRightOn : URLS.arrowRight;
  }

function keyToDir(key) {
  if (key === "ArrowUp") return "up";
  if (key === "ArrowDown") return "down";
  if (key === "ArrowLeft") return "left";
  if (key === "ArrowRight") return "right";
  return null;
}

  // ✅ NEW: allow typing in inputs (ex: win modal email) without game key capture
  function isTextEntryEl(el) {
    if (!el) return false;
    const tag = (el.tagName || "").toUpperCase();
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    if (el.isContentEditable) return true;
    return false;
  }

  function shouldIgnoreGameKeys(e) {
    const t = e?.target;
    if (isTextEntryEl(t)) return true;

    const ae = document.activeElement;
    if (isTextEntryEl(ae)) return true;

    // If focus is anywhere inside the win modal, never hijack keys.
    if (levelCompleteEl && !levelCompleteEl.hidden) {
      if (ae && levelCompleteEl.contains(ae)) return true;
      if (t && levelCompleteEl.contains(t)) return true;
    }

    return false;
  }

  let lastMoveAt = 0;
  const MOVE_COOLDOWN = 120;

  function tryMove(dir, now) {
    if (!dir) return;
    if (now - lastMoveAt < MOVE_COOLDOWN) return;
    moveEntity(player, dir);
    lastMoveAt = now;
  }

  window.addEventListener(
    "keydown",
    (e) => {
      if (shouldIgnoreGameKeys(e)) return;

      const dir = keyToDir(e.key);
      if (!dir) return;
      pressed[dir] = true;
      setArrowVisual(dir, true);
      e.preventDefault();
    },
    { passive: false }
  );

  window.addEventListener(
    "keyup",
    (e) => {
      if (shouldIgnoreGameKeys(e)) return;

      const dir = keyToDir(e.key);
      if (!dir) return;
      pressed[dir] = false;
      setArrowVisual(dir, false);
      e.preventDefault();
    },
    { passive: false }
  );

  // ---------------------------------------------------------------------------
  // ✅ MOBILE: block highlight/callouts/double-tap on controls
  // ---------------------------------------------------------------------------
  function hardBlockMobileGestures(el) {
    if (!el) return;
    el.style.userSelect = "none";
    el.style.webkitUserSelect = "none";
    el.style.webkitTouchCallout = "none";
    el.style.webkitUserDrag = "none";
    el.style.touchAction = "none";
    el.style.webkitTapHighlightColor = "rgba(0,0,0,0)";

    el.addEventListener("contextmenu", (e) => e.preventDefault(), { passive: false });

    // iOS Safari: needs passive:false to reliably stop selection/callouts
    el.addEventListener(
      "touchstart",
      (e) => {
        e.preventDefault();
      },
      { passive: false }
    );
    el.addEventListener(
      "touchend",
      (e) => {
        e.preventDefault();
      },
      { passive: false }
    );
  }

  // ---- Mobile buttons (touch + hold)
  // Keep your current behavior, but bind to BOTH img + button.
  // Also: FIX hold releasing by not treating pointerleave as “finger up” for touch.
  const controlsBound = new WeakSet();

  function setMobileHitArea() {
    const pairs = [
      { btn: btnUp, img: arrowUpImg },
      { btn: btnDown, img: arrowDownImg },
      { btn: btnLeft, img: arrowLeftImg },
      { btn: btnRight, img: arrowRightImg },
    ];

    pairs.forEach(({ btn, img }) => {
      if (!btn) return;

      // Always: button interactive, and block gestures
      btn.style.pointerEvents = "auto";
      btn.style.touchAction = "none";
      hardBlockMobileGestures(btn);

      if (isMobileLayout()) {
        // On mobile: allow image to be the actual touch target
        if (img) {
          img.style.pointerEvents = "auto";
          img.style.touchAction = "none";
          hardBlockMobileGestures(img);
        }
      } else {
        // Desktop: image is visual only (button is click target)
        if (img) {
          img.style.pointerEvents = "none";
          img.style.touchAction = "none";
          hardBlockMobileGestures(img);
        }
      }
    });
  }

  function bindTarget(target, dir) {
    if (!target) return;
    if (controlsBound.has(target)) return;
    controlsBound.add(target);

    const setPressed = (on) => {
      pressed[dir] = on;
      setArrowVisual(dir, on);
    };

    const down = (e) => {
      setPressed(true);
      try {
        if (e.pointerId != null) target.setPointerCapture?.(e.pointerId);
      } catch {}
      e.preventDefault();
      e.stopPropagation();
    };

    const up = (e) => {
      setPressed(false);
      try {
        if (e.pointerId != null) target.releasePointerCapture?.(e.pointerId);
      } catch {}
      e.preventDefault();
      e.stopPropagation();
    };

    target.addEventListener("contextmenu", (e) => e.preventDefault(), { passive: false });
    target.addEventListener("pointerdown", down, { passive: false });
    target.addEventListener("pointerup", up, { passive: false });
    target.addEventListener("pointercancel", up, { passive: false });
    target.addEventListener("lostpointercapture", up, { passive: false });

    // ✅ IMPORTANT:
    // Only use pointerleave to release for mouse/pen.
    // On touch, finger drift can trigger pointerleave even though user is holding.
    target.addEventListener(
      "pointerleave",
      (e) => {
        if (e.pointerType === "touch") return;
        up(e);
      },
      { passive: false }
    );

    // iOS fallback
    target.addEventListener(
      "touchstart",
      (e) => {
        down(e);
      },
      { passive: false }
    );
    target.addEventListener(
      "touchend",
      (e) => {
        up(e);
      },
      { passive: false }
    );
    target.addEventListener(
      "touchcancel",
      (e) => {
        up(e);
      },
      { passive: false }
    );
  }

  function ensureControlsBound() {
    setMobileHitArea();
    // bind to BOTH on mobile to avoid “one arrow broke” if an img is missing
    bindTarget(btnUp, "up");
    bindTarget(btnDown, "down");
    bindTarget(btnLeft, "left");
    bindTarget(btnRight, "right");

    bindTarget(arrowUpImg, "up");
    bindTarget(arrowDownImg, "down");
    bindTarget(arrowLeftImg, "left");
    bindTarget(arrowRightImg, "right");
  }

  // ---- Loop
  let lastToddlerAt = 0;
  let toddlerInterval = 260;

  function stopLoop() {
    running = false;
    if (rafId) cancelAnimationFrame(rafId);
    rafId = 0;
  }

  function startLoop() {
    if (running) return;
    running = true;
    rafId = requestAnimationFrame(tick);
  }

  function tick(now) {
    if (!running) return;

    spawnItems();

    if (pressed.up) tryMove("up", now);
    else if (pressed.down) tryMove("down", now);
    else if (pressed.left) tryMove("left", now);
    else if (pressed.right) tryMove("right", now);

    if (now - lastToddlerAt > toddlerInterval) {
      toddlerStep(now);
      lastToddlerAt = now;
    }

    checkPickups(now);
    checkHit();

    if (checkWin()) {
      stopLoop();
      clearPressedInput();

      if (completeBagImg) completeBagImg.src = LEVEL_BAG_IMAGES[level - 1];
      wireWinModalLinkForLevel(level);
      applyEndOfGameUI();
      applyWinModalLayout();
      if (levelCompleteEl) levelCompleteEl.hidden = false;
      return;
    }

    drawFrame(now);
    rafId = requestAnimationFrame(tick);
  }

  function applyLevelDifficulty() {
    const base = 260;
    const drop = (level - 1) * 22;
    toddlerInterval = Math.max(150, base - drop);
  }

  if (nextLevelBtn) {
    nextLevelBtn.addEventListener("click", () => {
      stopLoop();
      clearPressedInput();

      level = Math.min(6, level + 1);
      applyLevelDifficulty();
      resetLevel();
      drawFrame(performance.now());
      startLoop();
    });
  }

  if (replayBtn) {
    replayBtn.addEventListener("click", () => {
      stopLoop();
      clearPressedInput();

      applyLevelDifficulty();
      resetLevel();
      drawFrame(performance.now());
      startLoop();
    });
  }

  const onLayoutChange = () => {
    positionShowcase();
    positionDpad();
    positionHud();
    applyWinModalLayout();
    setMobileHitArea();
  };

  window.addEventListener("resize", onLayoutChange, { passive: true });
  if (mqMobile && mqMobile.addEventListener) {
    mqMobile.addEventListener("change", onLayoutChange);
  }

  startBtn.addEventListener("click", async (e) => {
    e.preventDefault();
    if (startBtn.disabled) return;

    applyHudLayout();
    startBtn.disabled = true;
    stage.hidden = false;

    if (levelCompleteEl) levelCompleteEl.hidden = true;

    await loadAll();
    ensureControlsBound();
    ensureSpriteShowcase();

    positionShowcase();
    positionDpad();
    positionHud();
    applyWinModalLayout();

    level = 1;
    applyLevelDifficulty();
    resetLevel();

    drawFrame(performance.now());
    startLoop();
  });
})();
